import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { environment } from '../../../../environments/environment';

import { Event } from '../../../models/event';

import { AuthService } from '../../../services/common/auth.service';
import { DataStoreService } from '../../../services/common/data-store.service';
import { EventService } from '../../../services/events/event.service';
import { NotyService } from '../../../services/common/noty.service';

@Component({
  selector: 'app-event-show',
  templateUrl: './event-show.component.html',
  styleUrls: ['./event-show.component.css'],
})
export class EventShowComponent implements OnInit, OnChanges {
  /**
   * Indicates if event edit option is shown
   */
  @Input() allowEdit: boolean = true;

  /**
   * Event id from a parent component
   */
  @Input() eventId: number | undefined;

  /**
   * Modal mode, used to show events as a modal
   */
  @Input() modalMode: boolean = false;

  /**
   * Output event id for editing
   * @param {EventEmitter<number> = new EventEmitter(} ) editEvent variable
   */
  @Output() editEvent: EventEmitter<number> = new EventEmitter();

  /**
   * Event being shown
   */
  event: Event = new Event();

  /**
   * Indicates if the page is loading
   * @type {boolean}
   */
  loading: boolean = true;

  /**
   * Indicates if production mode, based on which debug information is hidden
   * @type {boolean}
   */
  production: boolean = true;

  /**
   * Indicates if debug information for tech is to be shown
   * @type {boolean}
   */
  techDebug: boolean = false;

  /**
   * Constructor
   * @param {EventService}   private eventService Service to access events resource in the backend
   * @param {Location}       private location     Angular location object
   * @param {NotyService}    private notyService  Service for notifications in the web page
   * @param {ActivatedRoute} private route        Angular route object for current route
   */
  constructor(private authService: AuthService, private dataStoreService: DataStoreService, private eventService: EventService, private location: Location, private notyService: NotyService, private route: ActivatedRoute, private router: Router) {
    this.production = environment.production;
    this.authService.techDebug().then((techDebug) => {
      this.techDebug = techDebug;
    });
  }

  // Component lifecycle methods

  /**
   * Called when the component is initialized
   */
  ngOnInit() {
    if (!this.eventId && !this.modalMode) {
      this.eventId = Number(this.route.snapshot.params['id']);
      this.loadEvent();
    }
  }

  /**
   * Called when component variables change
   * @param {SimpleChanges} changes Angular SimpleChanges object with changes done
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.eventId && changes.eventId.currentValue) this.loadEvent();
  }

  // Component methods

  /**
   * Used to move back to previous page, used in non-modal usage only
   */
  back() {
    this.location.back();
  }

  /**
   * Edit event being shown
   */
  edit() {
    if (this.modalMode) this.editEvent.emit(this.eventId);
    else this.router.navigateByUrl(`/events?edit_event_id=${this.event.id}`);
  }

  /**
   * Open event location in Google Maps in a new tab/window
   */
  openAddress() {
    window.open(`http://local.google.com/maps?daddr=${this.event.address}`);
  }

  /**
   * Load event
   */
  loadEvent() {
    this.eventService
      .get(this.eventId!)
      .then((event) => {
        this.loading = false;
        if (event && event.id) this.event = event;
      })
      .catch(() => {
        this.loading = false;
        this.notyService.notify('Error getting event', 'error');
      });
  }

  /**
   * Open reminder using a reminder component
   * @param {number} reminder_id id of reminder to open
   */
  openReminder(reminder_id: number) {
    let reminderComponent: any = this.dataStoreService.retrieveStoreValues('reminderStore')[0];
    if (reminderComponent) reminderComponent.loadReminder(reminder_id, true);
    else this.notyService.notify('Error opening reminder', 'warning', 5000, 'center');
  }
}
